var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { BwElement } from './base/bw-element.js';
import { resolvePlacement } from './bw-dropdown-placement.js';
// Per-host prefix for option ids (aria-activedescendant needs stable, unique ids
// even with several dropdowns on a page).
let _hostSeq = 0;
let BwDropdown = class BwDropdown extends BwElement {
    constructor() {
        super(...arguments);
        this.size = 'md';
        this.value = '';
        this.placeholder = 'Select...';
        this.disabled = false;
        this.options = [];
        /** Several values at once (export window "Versions", 2026-10-07). Single
         *  mode keeps `value`; multiple mode reads and reports `values`. */
        this.multiple = false;
        this.values = [];
        /** Trigger text in multiple mode, e.g. "3 versions" — the consumer localises it.
         *  Pass `''` for the empty case and use `placeholder` for "Choose…": a non-empty
         *  summary is never styled as empty. */
        this.summary = '';
        /** Label of the close button at the bottom of a multiple popover. */
        this.doneLabel = '';
        /** Reflected `open` lets CSS target the trigger's "pushed in" state
         *  from the host attribute. The popover's own visibility rides on the
         *  `.is-on` class instead — see render(). */
        this.open = false;
        // Accessible name for the trigger. Callers set `aria-label` on the HOST, but the
        // interactive element is the inner `.bw-dd-trigger` button, which has no name of
        // its own — so AT never heard it. Same pattern as <bw-checkbox>: redeclare
        // `ariaLabel` (normally inherited from ARIAMixin) as a Lit property to observe
        // the attribute and forward it; `reflect: true` keeps it on the host as well.
        // Note it REPLACES the trigger's visible text as the name, so the current
        // value is not announced with it — give the label a noun, not a state.
        this.ariaLabel = null;
        this._focusedIdx = -1;
        this._uid = `bw-dd-${++_hostSeq}`;
        this._onDocPointer = (e) => {
            if (!this.open)
                return;
            const path = e.composedPath();
            if (path.includes(this))
                return;
            this._close();
        };
        this._onDocKey = (e) => {
            if (!this.open)
                return;
            if (e.key === 'Escape') {
                e.preventDefault();
                this._close();
                const t = this.querySelector('.bw-dd-trigger');
                t?.focus();
            }
        };
        this._toggle = (e) => {
            e.stopPropagation();
            if (this.open)
                this._close();
            else
                this._open();
        };
        this._onTriggerKey = (e) => {
            if (this.disabled)
                return;
            switch (e.key) {
                case 'Enter':
                case ' ':
                case 'ArrowDown':
                    e.preventDefault();
                    this._open();
                    break;
            }
        };
        this._onListKey = (e) => {
            if (!this.open)
                return;
            const len = this.options.length;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                let i = this._focusedIdx;
                for (let n = 0; n < len; n++) {
                    i = (i + 1) % len;
                    if (!this.options[i].disabled) {
                        this._focusedIdx = i;
                        return;
                    }
                }
            }
            else if (e.key === 'ArrowUp') {
                e.preventDefault();
                let i = this._focusedIdx;
                for (let n = 0; n < len; n++) {
                    i = (i - 1 + len) % len;
                    if (!this.options[i].disabled) {
                        this._focusedIdx = i;
                        return;
                    }
                }
            }
            else if (e.key === 'Home') {
                e.preventDefault();
                this._focusedIdx = this.options.findIndex((o) => !o.disabled);
            }
            else if (e.key === 'End') {
                e.preventDefault();
                for (let i = len - 1; i >= 0; i--) {
                    if (!this.options[i].disabled) {
                        this._focusedIdx = i;
                        return;
                    }
                }
            }
            else if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                // Two kinds of focus coexist: the list keeps a "virtual" active row in
                // `_focusedIdx` (arrow keys), but Tab or a click can put DOM focus on an
                // option button itself. When the key lands on an option, act on THAT one —
                // otherwise Enter toggles whichever row the arrows last visited.
                const onOption = e.target?.closest?.('.bw-dd-option');
                const at = onOption
                    ? Array.from(this.querySelectorAll('.bw-dd-option')).indexOf(onOption)
                    : this._focusedIdx;
                const opt = this.options[at];
                if (opt)
                    this._select(opt);
            }
        };
    }
    _optId(i) { return `${this._uid}-opt-${i}`; }
    _open() {
        if (this.disabled || this.open)
            return;
        this.open = true;
        // Multiple mode starts on the first ticked row, single mode on the chosen one.
        const idx = this.options.findIndex((o) => this._isSelected(o));
        this._focusedIdx = idx >= 0 ? idx : 0;
        document.addEventListener('pointerdown', this._onDocPointer);
        document.addEventListener('keydown', this._onDocKey);
    }
    _close() {
        if (!this.open)
            return;
        this.open = false;
        this._focusedIdx = -1;
        // Гигиена: атрибут не должен пережить открытие, к которому относится.
        // Атрибуты флипа переставляют точку привязки ТОЛЬКО у поповера, а закрытый
        // поповер невидим (волна 3: он остаётся в DOM, но инертен) — так что на вид
        // это не влияет; но `flip-y` на схлопнутом контроле читается как состояние,
        // которого нет, и путает и отладку, и тесты.
        //
        // ⚠️ Это НЕ защита от наследования чужой геометрии: `_place()` на каждом
        // открытии зовёт toggleAttribute с явным вторым аргументом, то есть и
        // ставит, и снимает сам. Прежняя редакция комментария утверждала обратное
        // (сквозное ревью 04.09).
        this.removeAttribute('flip-x');
        this.removeAttribute('flip-y');
        document.removeEventListener('pointerdown', this._onDocPointer);
        document.removeEventListener('keydown', this._onDocKey);
    }
    _isSelected(opt) {
        return this.multiple ? this.values.includes(opt.value) : opt.value === this.value;
    }
    _select(opt) {
        if (opt.disabled)
            return;
        if (this.multiple) {
            // Toggle one option; the result follows the OPTIONS order, not the click
            // order. The popover stays open — it closes by Done, a click outside or
            // Escape, so several versions can be ticked in one go.
            const next = this.options
                .filter((o) => (o.value === opt.value ? !this.values.includes(o.value) : this.values.includes(o.value)))
                .map((o) => o.value);
            this.values = next;
            this.dispatchEvent(new CustomEvent('bw-change', {
                detail: { values: next },
                bubbles: true,
                composed: true,
            }));
            return;
        }
        if (this.value !== opt.value) {
            this.value = opt.value;
            this.dispatchEvent(new CustomEvent('bw-change', {
                detail: { value: opt.value },
                bubbles: true,
                composed: true,
            }));
        }
        this._close();
        const t = this.querySelector('.bw-dd-trigger');
        t?.focus();
    }
    /**
     * Точка цвета опции. Краска едет через кастомное свойство, а не через
     * `background` инлайном: правило остаётся в CSS-файле вместе с размером и
     * формой, и потребитель не может случайно переопределить их стилем хоста.
     *
     * ⚠️ Значение едет через `styleMap`, а НЕ интерполяцией в атрибут `style`.
     * Интерполяция подставляет строку в атрибут целиком, поэтому краска с `;`
     * внутри дописала бы в тот же атрибут произвольные объявления. Источник
     * значения — `section.color` из song.json и из импорта Ableton, то есть не
     * строго палитра приложения. `styleMap` для имён с дефисом зовёт
     * `style.setProperty`, а тот негодное значение отвергает целиком. Тот же
     * приём у свотча заголовка на десктопе (chart-read.js, `setProperty`).
     */
    _dot(color) {
        if (!color)
            return '';
        return html `<span
      class="bw-dd-option-dot"
      style=${styleMap({ '--bw-dd-dot': color })}
      aria-hidden="true"
    ></span>`;
    }
    _renderOption(opt, i) {
        const multiple = this.multiple;
        return html `
          <button
            type="button"
            class="bw-dd-option"
            role="option"
            id=${this._optId(i)}
            aria-selected=${this._isSelected(opt) ? 'true' : 'false'}
            ?disabled=${opt.disabled ?? false}
            data-focused=${i === this._focusedIdx ? 'true' : 'false'}
            @click=${(e) => { e.stopPropagation(); this._select(opt); }}
            @mouseenter=${() => { if (!opt.disabled)
            this._focusedIdx = i; }}
            @focus=${() => { if (!opt.disabled)
            this._focusedIdx = i; }}
          >
            ${multiple ? html `<span class="bw-dd-option-box" aria-hidden="true"></span>` : ''}
            ${this._dot(opt.color)}
            <span class="bw-dd-option-label">${opt.label}</span>
            ${!multiple && this._isSelected(opt)
            ? html `<span class="bw-dd-option-check" aria-hidden="true">✓</span>`
            : ''}
          </button>
        `;
    }
    render() {
        const multiple = this.multiple;
        const current = multiple ? undefined : this.options.find((o) => o.value === this.value);
        const picked = multiple ? this.options.filter((o) => this.values.includes(o.value)) : [];
        const label = multiple
            ? (this.summary || picked.map((o) => o.label).join(', ') || this.placeholder)
            : (current ? current.label : this.placeholder);
        const empty = multiple ? (!this.summary && picked.length === 0) : !current;
        const options = this.options.map((opt, i) => this._renderOption(opt, i));
        // A closed list has no active descendant, whatever `_focusedIdx` still holds.
        const active = this.open && this._focusedIdx >= 0 && this._focusedIdx < this.options.length
            ? this._optId(this._focusedIdx)
            : nothing;
        return html `
      <button
        type="button"
        class="bw-dd-trigger"
        ?disabled=${this.disabled}
        aria-haspopup="listbox"
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-label=${this.ariaLabel ?? nothing}
        @click=${this._toggle}
        @keydown=${this._onTriggerKey}
      >
        ${this._dot(current?.color)}
        <span class="bw-dd-label" data-empty=${empty}>${label}</span>
        <span class="bw-dd-chevron" aria-hidden="true">
          <svg viewBox="0 0 12 12" width="10" height="10"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
      </button>
      ${multiple
            // Multiple mode: the popover is a plain container. A listbox may own only
            // options and groups, so the listbox is the inner list and the Done button
            // sits OUTSIDE it — which also keeps Done in view while a long list scrolls.
            ? html `
      <div
        class="bw-dd-popover ${this.open ? 'is-on' : ''}"
        aria-hidden=${this.open ? nothing : 'true'}
        ?inert=${!this.open}
      >
        <div
          class="bw-dd-list"
          role="listbox"
          aria-multiselectable="true"
          aria-activedescendant=${active}
          @keydown=${this._onListKey}
          tabindex="-1"
        >${options}</div>
        ${this.doneLabel
                ? html `<button
              type="button"
              class="bw-dd-done"
              @click=${(e) => {
                    e.stopPropagation();
                    this._close();
                    this.querySelector('.bw-dd-trigger')?.focus();
                }}
            >${this.doneLabel}</button>`
                : ''}
      </div>`
            : html `
      <div
        class="bw-dd-popover ${this.open ? 'is-on' : ''}"
        role="listbox"
        aria-activedescendant=${active}
        aria-hidden=${this.open ? nothing : 'true'}
        ?inert=${!this.open}
        @keydown=${this._onListKey}
        tabindex="-1"
      >
        ${options}
      </div>`}
    `;
    }
    updated(changed) {
        // Пересчёт стороны — и на открытии, и на смене СПИСКА у уже открытого:
        // от числа опций зависит высота поповера, а от неё — решение про flip-y.
        // chart-read.js пересобирает `options` на каждую отрисовку листа, так что
        // это не гипотетический случай (сквозное ревью 04.09).
        if (this.open && (changed.has('open') || changed.has('options'))) {
            // Расположение считается ПОСЛЕ отрисовки поповера — до неё мерить нечего.
            this._place();
        }
        if (changed.has('open') && this.open) {
            // Focus the listbox so keyboard navigation works immediately — the inner
            // list in multiple mode, the popover itself in single mode.
            queueMicrotask(() => {
                const list = this.querySelector(this.multiple ? '.bw-dd-list' : '.bw-dd-popover');
                list?.focus();
            });
        }
    }
    /**
     * Выбрать сторону раскрытия по свободному месту во вьюпорте.
     *
     * Сторона держится АТРИБУТОМ напрямую, а не реактивным свойством с reflect,
     * в отличие от соседнего `open`. Причина: `open` управляет тем, ЧТО
     * рендерится (есть поповер или нет), а сторона разметку не меняет вовсе —
     * только CSS. Реактивное свойство, присвоенное внутри updated(), завело бы
     * лишний цикл отрисовки и предупреждение Lit «an update was scheduled after
     * an update completed».
     *
     * Размер поповера от стороны НЕ зависит (меняется только точка привязки),
     * поэтому обратной связи «флипнули → пересчитали → расфлипнули» здесь нет и
     * повторный замер не нужен.
     */
    _place() {
        const pop = this.querySelector('.bw-dd-popover');
        // Поповера нет — мерить нечего. С волны 3 он смонтирован всегда, так что
        // единственный путь сюда — вызов _place() до первой отрисовки. Проверки на
        // сам getBoundingClientRect здесь НЕТ намеренно — он есть у любого
        // HTMLElement в любой среде, где компонент вообще регистрируется, и
        // «защита» от него только маскировала бы поломку окружения (сквозное
        // ревью 04.09).
        if (!pop)
            return;
        const host = this.getBoundingClientRect();
        const p = pop.getBoundingClientRect();
        // ⚠️ The popover's SIZE is read from the layout box (offsetWidth/Height),
        // not from the rect. Since perf wave 3 the popover carries an entry
        // transform, and this measurement happens synchronously from updated(),
        // i.e. on the very first frame of that transition — the rect is therefore
        // still the `scale(.96)` start value. Measured in Chromium: a 120×152
        // popover reports 115.20×145.92 here and 120×152 two frames later, while
        // offsetWidth/offsetHeight report the true 120×152 immediately because the
        // layout box ignores transforms. Off by 4 % is enough to matter: the flip
        // rule keeps an 8px gutter to the viewport edge, and a 280px-tall popover
        // under-measured by 4 % eats 11px of it.
        //
        // Zero means NOT MEASURED, not "zero-sized" — happy-dom computes no layout
        // at all — so it falls back to the rect rather than handing resolvePlacement
        // zeros, which would disable flipping outright.
        const width = pop.offsetWidth || p.width;
        const height = pop.offsetHeight || p.height;
        const { flipX, flipY } = resolvePlacement({
            host: { left: host.left, top: host.top, right: host.right, bottom: host.bottom },
            popover: { width, height },
            viewport: { width: window.innerWidth, height: window.innerHeight },
        });
        this.toggleAttribute('flip-x', flipX);
        this.toggleAttribute('flip-y', flipY);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('pointerdown', this._onDocPointer);
        document.removeEventListener('keydown', this._onDocKey);
    }
};
__decorate([
    property({ type: String, reflect: true })
], BwDropdown.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwDropdown.prototype, "value", void 0);
__decorate([
    property({ type: String })
], BwDropdown.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwDropdown.prototype, "disabled", void 0);
__decorate([
    property({ attribute: false })
], BwDropdown.prototype, "options", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwDropdown.prototype, "multiple", void 0);
__decorate([
    property({ attribute: false })
], BwDropdown.prototype, "values", void 0);
__decorate([
    property({ type: String })
], BwDropdown.prototype, "summary", void 0);
__decorate([
    property({ type: String, attribute: 'done-label' })
], BwDropdown.prototype, "doneLabel", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwDropdown.prototype, "open", void 0);
__decorate([
    property({ type: String, attribute: 'aria-label', reflect: true })
], BwDropdown.prototype, "ariaLabel", void 0);
__decorate([
    state()
], BwDropdown.prototype, "_focusedIdx", void 0);
BwDropdown = __decorate([
    customElement('bw-dropdown')
], BwDropdown);
export { BwDropdown };
//# sourceMappingURL=bw-dropdown.js.map